import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle, faUserGraduate, faFlag } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface InstructorStudent {
  userId: string;
  studentRecordId: string;
  enrollmentId: string;
  studentId: string;
  fullName: string;
  email: string;
  learnershipTitle: string;
  overallProgressPercent: number;
  averageGrade: number | null;
  overrideGrade: number | null;
  isFlagged: boolean;
}

export default function InstructorStudentsPage() {
  const [students, setStudents] = useState<InstructorStudent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/api/instructor/students', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load students');
        setStudents(await response.json());
      } catch (err) {
        console.error(err);
        setError('Failed to load your students. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStudents();
  }, [accessToken]);

  const filtered = students.filter(
    (s) =>
      s.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.studentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.learnershipTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Students</h1>
        <p className="text-sm text-brand-900/60 mt-1">Every student enrolled across your assigned courses</p>
      </div>

      <div className="p-8">
        {!isLoading && !error && (
          <div className="mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, student ID, or course..."
              className="w-full max-w-md px-4 py-2.5 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faUserGraduate} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No students found</h3>
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="card-surface overflow-hidden">
            <div className="hidden md:grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
              <span>Student</span>
              <span>Course</span>
              <span>Progress</span>
              <span>Average</span>
              <span></span>
            </div>
            <div className="divide-y divide-canvas-200">
              {filtered.map((s) => (
                <div
                  key={s.enrollmentId}
                  className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                >
                  <div className="min-w-0 flex items-center gap-2">
                    {s.isFlagged && <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-950 truncate">{s.fullName}</p>
                      <p className="text-xs text-brand-900/50 truncate">{s.studentId}</p>
                    </div>
                  </div>
                  <p className="text-sm text-brand-900/70 truncate">{s.learnershipTitle}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-canvas-200 rounded-full h-1.5">
                      <div className="bg-brand-gradient h-1.5 rounded-full" style={{ width: `${s.overallProgressPercent}%` }} />
                    </div>
                    <span className="text-xs text-brand-900/60">{s.overallProgressPercent}%</span>
                  </div>
                  <p className="text-sm text-brand-900/70">
                    {s.overrideGrade != null ? `${s.overrideGrade}%` : s.averageGrade != null ? `${s.averageGrade}%` : '—'}
                  </p>
                  <Link
                    to={`/instructor/students/${s.studentRecordId}`}
                    className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors w-fit"
                  >
                    View
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
